/**
 * Typed API clients for the 4 AURA Meeting-Mode calculators + the
 * calc-sourced proposals endpoint.
 *
 * Each client follows the same shape (`ApiClient<TInput, TResult>`) so the
 * 1:1-port calculator pages + ConvertToProposalButton can be plugged
 * against any of the 4 calculators without per-calc branching.
 *
 * Backend reference (route prefixes — see backend/src/index.ts):
 *   /api/v1/ss-tank
 *   /api/v1/domestic-heat-pump
 *   /api/v1/commercial-heat-pump
 *   /api/v1/pool-heat-pump
 *   /api/v1/proposals                    (calc-sourced create — proposals.routes.ts)
 *   /api/v1/calculators                  (registry — discoverability)
 */
import { api } from '@/utils/api';
import type {
  CalculatorKey,
  CommercialHpInput,
  CommercialHpResult,
  DomesticHpInput,
  DomesticHpResult,
  PoolInput,
  PoolResult,
  PoolCalculateResponse,
  PoolModelEnvelopeInput,
  PoolModelEnvelopeResult,
  RoiComparison,
  TankInput,
  TankResult,
  CustomizedTankInput,
  CustomizedTankResult,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Common shapes
// ─────────────────────────────────────────────────────────────────────────────

export interface MeetingSession {
  /** JWT (or opaque) token bound to the meeting-mode session. */
  token: string;
  /** Persistent DB row id for the session — used for audit / quotation back-refs. */
  sessionId: string;
  /** ISO timestamp the token expires at. */
  expiresAt: string;
}

export interface SaveQuotationOutcome {
  id: string;
  createdAt: string;
}

export interface ApiClient<TInput, TResult> {
  startSession(leadId?: string | null): Promise<MeetingSession>;
  calculate(input: TInput, token: string): Promise<TResult>;
  saveQuotation(input: TInput, result: TResult, token: string): Promise<SaveQuotationOutcome>;
  getQuotation(id: string): Promise<unknown>;
  listCatalog(): Promise<unknown[]>;
}

// Backend responses are wrapped as `{ success, data }` — unwrap consistently.
function unwrap<T>(payload: any): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return payload.data as T;
  }
  return payload as T;
}

/**
 * Factory for an ApiClient bound to a specific calculator route prefix.
 * Each backend exposes the same surface (post /meeting-sessions, post /calculate,
 * post /quotations, etc.) so we centralise the wiring here.
 *
 * The `sessionResponseTokenKey` is a quirk of legacy responses — pool/domestic/
 * commercial wrap the token under `meetingSessionToken`, while newer routes
 * may simply use `token`. We accept both.
 */
interface ClientConfig {
  /** e.g. `/ss-tank`, `/pool-heat-pump` (NO `/api/v1` prefix — axios baseURL adds it). */
  prefix: string;
  /** Path for the catalog list endpoint. Defaults to `/catalog`. SS Tank uses `/catalog/standard-sizes`. */
  catalogPath?: string;
  /**
   * Full path for the calculate endpoint. Defaults to `${prefix}/calculate`.
   * Commercial Heat Pump v2 router uses `/sizing` instead — override here.
   */
  calculatePath?: string;
}

function makeClient<TInput, TResult>(cfg: ClientConfig): ApiClient<TInput, TResult> {
  const catalogPath = cfg.catalogPath ?? '/catalog';
  return {
    async startSession(leadId) {
      const res = await api.post(`${cfg.prefix}/meeting-sessions`, {
        leadId: leadId ?? null,
      });
      const data = unwrap<any>(res.data);
      const token = data.token ?? data.meetingSessionToken;
      if (!token) {
        throw new Error('Calculator session response missing token / meetingSessionToken');
      }
      return {
        token,
        sessionId: data.sessionId,
        expiresAt: data.expiresAt,
      };
    },

    async calculate(input, token) {
      // Backend route schemas (e.g. ss-tank calculateBodySchema) expect a FLAT
      // body — fields at top level, NOT wrapped under `{ input: {...} }`.
      // Bug discovered 2026-05-12 chrome smoke (all 4 calcs rejected w/ 400
      // "field: ... message: Required"). Fix: spread input directly.
      const res = await api.post(
        cfg.calculatePath ?? `${cfg.prefix}/calculate`,
        { ...input, meetingSessionToken: token },
        {
          headers: token ? { 'X-Meeting-Session-Token': token } : undefined,
        }
      );
      return unwrap<TResult>(res.data);
    },

    async saveQuotation(input, result, token) {
      // Backend quotationCreateSchema expects `inputs` (PLURAL) + `result`.
      // Frontend was previously sending `input` (singular) which silently
      // fails Zod validation. Fix: rename to `inputs`.
      const res = await api.post(
        `${cfg.prefix}/quotations`,
        { inputs: input, result, meetingSessionToken: token },
        {
          headers: token ? { 'X-Meeting-Session-Token': token } : undefined,
        }
      );
      return unwrap<SaveQuotationOutcome>(res.data);
    },

    async getQuotation(id) {
      const res = await api.get(`${cfg.prefix}/quotations/${encodeURIComponent(id)}`);
      return unwrap(res.data);
    },

    async listCatalog() {
      const res = await api.get(`${cfg.prefix}${catalogPath}`);
      const data = unwrap<any>(res.data);
      return Array.isArray(data) ? data : (data?.items ?? []);
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 4 concrete calculator clients
// ─────────────────────────────────────────────────────────────────────────────

// ─── Mode-A ("pick-a-model") shared shapes ───────────────────────────────────
export interface SsTankPreselectOption { sku: string; capacityL: number; grade: string; label: string; }
export interface DhpPreselectOption { value: string; kind: 'model' | 'series'; label: string; }
export interface PreselectBody { qty: number; leadId?: string | null; dealId?: string | null; projectName?: string | null; }
export interface DhpOverCeiling { modelLabel: string; breaches: string[]; disclaimer: string; }
export interface PreselectOutcome extends SaveQuotationOutcome { mode?: string; overCeiling?: DhpOverCeiling | null; }

export const ssTankApi = {
  ...makeClient<TankInput, TankResult>({
    prefix: '/ss-tank',
    // SS Tank exposes the standard-sizes list under /catalog/standard-sizes
    catalogPath: '/catalog/standard-sizes',
  }),
  async preselectOptions(): Promise<SsTankPreselectOption[]> {
    const res = await api.get('/ss-tank/preselect-options');
    return unwrap<any>(res.data)?.options ?? [];
  },
  async preselect(body: PreselectBody & { sku: string }): Promise<PreselectOutcome> {
    const res = await api.post('/ss-tank/preselect-quotation', body);
    return unwrap<PreselectOutcome>(res.data);
  },
};

export const domesticHpApi = {
  ...makeClient<DomesticHpInput, DomesticHpResult>({
    prefix: '/domestic-heat-pump',
  }),
  async preselectOptions(): Promise<DhpPreselectOption[]> {
    const res = await api.get('/domestic-heat-pump/preselect-options');
    return unwrap<any>(res.data)?.options ?? [];
  },
  async preselect(
    body: PreselectBody & { selection: string; profile?: Record<string, unknown> },
  ): Promise<PreselectOutcome> {
    const res = await api.post('/domestic-heat-pump/preselect-quotation', body);
    return unwrap<PreselectOutcome>(res.data);
  },
};

export const customizedTankApi: ApiClient<CustomizedTankInput, CustomizedTankResult> =
  makeClient<CustomizedTankInput, CustomizedTankResult>({
    prefix: '/customized-tank',
  });

export const commercialHpApi: ApiClient<CommercialHpInput, CommercialHpResult> =
  makeClient<CommercialHpInput, CommercialHpResult>({
    prefix: '/commercial-heat-pump',
    // v2 router exposes POST /sizing (not /calculate). See backend
    // modules/sales/commercial-heat-pump.routes.ts.
    calculatePath: '/commercial-heat-pump/sizing',
  });

const _poolBase = makeClient<PoolInput, PoolResult>({
  prefix: '/pool-heat-pump',
});

/**
 * Pool API surface — base ApiClient + reverse/ROI/save helpers for the
 * forward-and-reverse PoolCalculatorPage (Task 3.6 reference port).
 *
 *  - `.calculate(input, token)` → returns the full backend envelope
 *    `{ success, sizing, roi, roi_full, capex_basis }` verbatim (post
 *    Task 3.6 the route emits vendor-shape sizing + spec-shape ROI page;
 *    no client-side normalisation needed).
 *  - `.modelEnvelope({ model_id, climate_zone, placement, ... })` → reverse
 *    mode: configuration brief + 3×5 max-volume grid + sweet-spot +
 *    operating-limits + optional fit_check.
 *  - `.saveQuotation(input, response, token)` → persists the full vendor
 *    sizing result on `CalculatorQuotation.result` for downstream PDF +
 *    Convert-to-Proposal.
 *  - `.roi(...)` standalone — kept for the legacy 4-fuel comparison call.
 */
export const poolApi = {
  ..._poolBase,

  async calculate(input: PoolInput, token: string): Promise<PoolCalculateResponse> {
    const res = await api.post(
      `/pool-heat-pump/calculate`,
      { ...input, meetingSessionToken: token },
      {
        headers: token ? { 'X-Meeting-Session-Token': token } : undefined,
      }
    );
    // Route returns the full envelope at the top level — no `{ data: ... }`
    // wrap. unwrap() would no-op since there's no `.data` key, but we pass
    // the raw payload to keep the contract obvious to readers.
    return res.data as PoolCalculateResponse;
  },

  async saveQuotation(
    input: PoolInput,
    response: PoolCalculateResponse | PoolResult,
    token: string
  ) {
    // Save the vendor-shape sizing as `result` so downstream consumers
    // (PDF builder, lineItemsFrom, Convert-to-Proposal) all read the same
    // shape. If a bare PoolResult is passed (no wrapper), use it directly.
    const result =
      response && typeof response === 'object' && 'sizing' in response
        ? (response as PoolCalculateResponse).sizing
        : (response as PoolResult);
    const roiFull =
      response && typeof response === 'object' && 'roi_full' in response
        ? (response as PoolCalculateResponse).roi_full
        : undefined;
    const res = await api.post(
      `/pool-heat-pump/quotations`,
      {
        inputs: input,
        result: roiFull ? { ...result, roi_full: roiFull } : result,
        meetingSessionToken: token,
      },
      {
        headers: token ? { 'X-Meeting-Session-Token': token } : undefined,
      }
    );
    return unwrap<SaveQuotationOutcome>(res.data);
  },

  async modelEnvelope(
    body: PoolModelEnvelopeInput,
    token?: string
  ): Promise<PoolModelEnvelopeResult> {
    const res = await api.post(
      `/pool-heat-pump/model-envelope`,
      { ...body, meetingSessionToken: token },
      {
        headers: token ? { 'X-Meeting-Session-Token': token } : undefined,
      }
    );
    return res.data as PoolModelEnvelopeResult;
  },

  async roi(input: PoolInput, token?: string): Promise<RoiComparison> {
    const res = await api.post(
      `/pool-heat-pump/roi`,
      { input },
      {
        headers: token ? { 'X-Meeting-Session-Token': token } : undefined,
      }
    );
    return unwrap<RoiComparison>(res.data);
  },
};

/** Look up a client by calculatorKey — handy for the generic Wizard. */
export function clientFor(key: CalculatorKey): ApiClient<unknown, unknown> {
  switch (key) {
    case 'ss_tank':
      return ssTankApi as unknown as ApiClient<unknown, unknown>;
    case 'domestic_heat_pump':
      return domesticHpApi as unknown as ApiClient<unknown, unknown>;
    case 'commercial_heat_pump':
      return commercialHpApi as unknown as ApiClient<unknown, unknown>;
    case 'pool_heat_pump':
      return _poolBase as unknown as ApiClient<unknown, unknown>;
    case 'customized_tank':
      return customizedTankApi as unknown as ApiClient<unknown, unknown>;
    default: {
      const exhaustive: never = key;
      throw new Error(`Unknown calculatorKey: ${String(exhaustive)}`);
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Proposals API (calc-sourced)
// ─────────────────────────────────────────────────────────────────────────────

export interface CreateProposalRequest {
  calculatorKey: CalculatorKey;
  quotationId: string;
  projectName: string;
  leadId?: string | null;
  accountId?: string | null;
  notes?: string | null;
}

export interface CreateProposalResponse {
  id: string;
  proposalNumber: string;
}

export interface ListProposalsParams {
  leadId?: string;
  calculatorKey?: CalculatorKey;
  status?: string;
  page?: number;
  limit?: number;
}

export const proposalsApi = {
  async createFromQuotation(opts: CreateProposalRequest): Promise<CreateProposalResponse> {
    const res = await api.post('/proposals', opts);
    const data = unwrap<CreateProposalResponse>(res.data);
    return data;
  },

  async list(params: ListProposalsParams = {}) {
    const res = await api.get('/proposals', { params });
    return unwrap(res.data);
  },

  async get(id: string) {
    const res = await api.get(`/proposals/${encodeURIComponent(id)}`);
    return unwrap(res.data);
  },

  async updateStatus(id: string, status: string) {
    const res = await api.patch(`/proposals/${encodeURIComponent(id)}/status`, { status });
    return unwrap(res.data);
  },
};

export type {
  CalculatorKey,
  TankInput,
  TankResult,
  DomesticHpInput,
  DomesticHpResult,
  CommercialHpInput,
  CommercialHpResult,
  PoolInput,
  PoolResult,
  PoolCalculateResponse,
  PoolRoiFull,
  PoolConfiguration,
  PoolModelEnvelopeInput,
  PoolModelEnvelopeResult,
  PoolCatalogModel,
  RoiComparison,
} from './types';
